import React, { useState, useCallback, useEffect, useRef, ChangeEvent } from 'react';
import { Box, Typography, IconButton, TextField, SxProps, Theme } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useSortable, SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { CustomTextField } from '../TextInput/CustomTextField';

export interface IDraggableFieldList {
    items: string[];
    onItemsChange: (items: string[]) => void;
    placeholder?: string;
    addButtonText?: string;
    maxItems?: number;
    disabled?: boolean;
    /** Return an error string if the value is invalid, or null/undefined if valid. */
    validate?: (value: string) => string | null | undefined;
    /** When set, uses a single-border MUI TextField instead of CustomTextField (brand/editorial modals). */
    inputSx?: SxProps<Theme>;
}

const DraggableFieldItem = ({
    item,
    index,
    themeStyle,
    onRemove,
    onUpdate,
    disabled,
    isEditing,
    onStartEdit,
    onCancelEdit,
    validate,
    inputSx,
}: {
    item: string;
    index: number;
    themeStyle: 'light' | 'dark';
    onRemove: () => void;
    onUpdate: (next: string) => void;
    disabled?: boolean;
    isEditing: boolean;
    onStartEdit: () => void;
    onCancelEdit: () => void;
    validate?: (value: string) => string | null | undefined;
    inputSx?: SxProps<Theme>;
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: `${index}-${item}`,
        disabled: disabled || isEditing,
    });
    const [editValue, setEditValue] = useState(item);
    const inputRef = useRef<HTMLInputElement>(null);
    const cancellingRef = useRef(false);

    useEffect(() => {
        if (isEditing) {
            cancellingRef.current = false;
            setEditValue(item);
            // Focus after paint so the field is mounted
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [isEditing, item]);

    const editError = editValue.trim() && validate ? validate(editValue.trim()) : null;

    const commitEdit = () => {
        if (cancellingRef.current) {
            cancellingRef.current = false;
            return;
        }
        const trimmed = editValue.trim();
        if (!trimmed || editError) {
            onCancelEdit();
            return;
        }
        if (trimmed !== item) {
            onUpdate(trimmed);
        } else {
            onCancelEdit();
        }
    };

    const cancelEdit = () => {
        cancellingRef.current = true;
        onCancelEdit();
    };

    return (
        <Box
            ref={setNodeRef}
            sx={{
                transform: CSS.Transform.toString(transform),
                transition,
                display: 'flex',
                alignItems: isEditing ? 'flex-start' : 'center',
                mb: 1,
                p: 1.5,
                backgroundColor: theme[themeStyle].gray_text,
                borderRadius: '8px',
                border: `1px solid ${isEditing ? theme[themeStyle].highlight : theme[themeStyle].placeholderGray50}`,
                '&:hover': { backgroundColor: theme[themeStyle].form_black },
            }}>
            <Box
                {...(isEditing ? {} : listeners)}
                {...(isEditing ? {} : attributes)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 1,
                    mt: isEditing ? 0.75 : 0,
                    cursor: disabled || isEditing ? 'default' : 'grab',
                    color: theme[themeStyle].tag_white,
                    '&:active': { cursor: disabled || isEditing ? 'default' : 'grabbing' },
                    opacity: disabled || isEditing ? 0.6 : 1,
                    flexShrink: 0,
                }}>
                <DragIndicatorIcon />
            </Box>

            {isEditing ? (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {inputSx ? (
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            value={editValue}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitEdit();
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelEdit();
                                }
                            }}
                            error={Boolean(editError)}
                            helperText={editError ?? undefined}
                            FormHelperTextProps={{
                                sx: {
                                    mx: 0,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: '0.75rem',
                                },
                            }}
                            sx={inputSx}
                        />
                    ) : (
                        <>
                            <CustomTextField
                                textFieldProps={{
                                    inputRef,
                                    InputProps: {
                                        disableUnderline: true,
                                        value: editValue,
                                        onChange: (e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value),
                                        onBlur: commitEdit,
                                        style: {
                                            borderRadius: '8px',
                                            color: theme[themeStyle].lightWhite,
                                        },
                                        onKeyDown: (e: React.KeyboardEvent) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                commitEdit();
                                            } else if (e.key === 'Escape') {
                                                e.preventDefault();
                                                cancelEdit();
                                            }
                                        },
                                    },
                                }}
                                mainSx={{
                                    borderWidth: editError ? 0 : 2,
                                    outline: editError ? '2px solid #f44336' : undefined,
                                    borderRadius: '8px',
                                }}
                            />
                            {editError && (
                                <Typography
                                    sx={{
                                        mt: 0.75,
                                        fontSize: '0.75rem',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: '#f44336',
                                    }}>
                                    {editError}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            ) : (
                <Typography
                    onClick={() => {
                        if (!disabled) onStartEdit();
                    }}
                    sx={{
                        flex: 1,
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        cursor: disabled ? 'default' : 'text',
                        userSelect: 'none',
                        '&:hover': disabled
                            ? undefined
                            : { textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.25)' },
                    }}>
                    {item}
                </Typography>
            )}

            {!disabled && !isEditing && (
                <IconButton
                    onClick={onRemove}
                    size="small"
                    sx={{
                        color: theme[themeStyle].tag_white,
                        '&:hover': { color: theme[themeStyle].highlight },
                    }}>
                    <CloseIcon />
                </IconButton>
            )}
        </Box>
    );
};

const DraggableFieldList: React.FC<IDraggableFieldList> = ({
    items,
    onItemsChange,
    placeholder = 'Add new item...',
    addButtonText = 'Add',
    maxItems,
    disabled = false,
    validate,
    inputSx,
}) => {
    const { themeStyle } = useThemeContext();
    // Require a small drag distance so a tap can start inline edit instead of dragging
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
    );
    const [newItem, setNewItem] = useState('');
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    const inputError = newItem.trim() && validate ? validate(newItem.trim()) : null;

    const handleAddItem = useCallback(() => {
        if (newItem.trim() && !disabled && !inputError) {
            if (maxItems && items.length >= maxItems) {
                return;
            }
            const updatedItems = [...items, newItem.trim()];
            onItemsChange(updatedItems);
            setNewItem('');
            setEditingIndex(null);
        }
    }, [newItem, items, onItemsChange, maxItems, disabled, inputError]);

    const handleRemoveItem = useCallback(
        (index: number) => {
            if (disabled) return;
            const updatedItems = items.filter((_, i) => i !== index);
            onItemsChange(updatedItems);
            setEditingIndex(prev => {
                if (prev === null) return null;
                if (prev === index) return null;
                if (prev > index) return prev - 1;
                return prev;
            });
        },
        [items, onItemsChange, disabled],
    );

    const handleUpdateItem = useCallback(
        (index: number, next: string) => {
            if (disabled) return;
            const updatedItems = [...items];
            updatedItems[index] = next;
            onItemsChange(updatedItems);
            setEditingIndex(null);
        },
        [items, onItemsChange, disabled],
    );

    const handleDragEnd = useCallback(
        (event: any) => {
            if (disabled) return;
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            const oldIndex = items.findIndex((item, i) => `${i}-${item}` === active.id);
            const newIndex = items.findIndex((item, i) => `${i}-${item}` === over.id);
            if (oldIndex < 0 || newIndex < 0) return;

            onItemsChange(arrayMove(items, oldIndex, newIndex));
            setEditingIndex(null);
        },
        [items, onItemsChange, disabled],
    );

    const canAddMore = !maxItems || items.length < maxItems;
    const sortableIds = items.map((item, index) => `${index}-${item}`);

    return (
        <Box sx={{ width: '100%' }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    {items.map((item, index) => (
                        <DraggableFieldItem
                            key={sortableIds[index]}
                            item={item}
                            index={index}
                            themeStyle={themeStyle}
                            onRemove={() => handleRemoveItem(index)}
                            onUpdate={next => handleUpdateItem(index, next)}
                            disabled={disabled}
                            isEditing={editingIndex === index}
                            onStartEdit={() => setEditingIndex(index)}
                            onCancelEdit={() => setEditingIndex(null)}
                            validate={validate}
                            inputSx={inputSx}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            {!disabled && canAddMore && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: inputSx ? 'flex-start' : 'center' }}>
                        {inputSx ? (
                            <TextField
                                fullWidth
                                placeholder={placeholder}
                                value={newItem}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewItem(e.target.value)}
                                onKeyDown={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddItem();
                                    }
                                }}
                                error={Boolean(inputError)}
                                helperText={inputError ?? undefined}
                                FormHelperTextProps={{
                                    sx: {
                                        mx: 0,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: '0.75rem',
                                    },
                                }}
                                sx={inputSx}
                            />
                        ) : (
                            <CustomTextField
                                placeholder={placeholder}
                                textFieldProps={{
                                    InputProps: {
                                        disableUnderline: true,
                                        value: newItem,
                                        onChange: (e: ChangeEvent<HTMLInputElement>) => setNewItem(e.target.value),
                                        style: {
                                            borderRadius: '8px',
                                            color: theme[themeStyle].lightWhite,
                                        },
                                        onKeyPress: (e: React.KeyboardEvent) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddItem();
                                            }
                                        },
                                    },
                                }}
                                mainSx={{
                                    borderWidth: inputError ? 0 : 2,
                                    outline: inputError ? '2px solid #f44336' : undefined,
                                    borderRadius: '8px',
                                }}
                            />
                        )}
                        <IconButton
                            onClick={handleAddItem}
                            disabled={!newItem.trim() || Boolean(inputError)}
                            aria-label={addButtonText}
                            sx={{
                                ml: 1,
                                mt: inputSx ? 0.75 : 0,
                                flexShrink: 0,
                                color: theme[themeStyle].tag_white,
                                '&:hover': {
                                    color: theme[themeStyle].highlight,
                                },
                                '&:disabled': {
                                    color: theme[themeStyle].form_label,
                                },
                            }}>
                            <AddIcon />
                        </IconButton>
                    </Box>
                    {!inputSx && inputError && (
                        <Typography
                            sx={{
                                mt: 0.75,
                                fontSize: '0.75rem',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: '#f44336',
                            }}>
                            {inputError}
                        </Typography>
                    )}
                    {!inputError && (
                        <Typography
                            sx={{
                                mt: 0.75,
                                fontSize: '0.75rem',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].form_label,
                            }}>
                            Press Enter or + to add a new line
                        </Typography>
                    )}
                </Box>
            )}

            {maxItems && items.length >= maxItems && (
                <Typography
                    sx={{
                        mt: 1,
                        color: theme[themeStyle].form_label,
                        fontSize: '0.75rem',
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    Maximum {maxItems} items allowed
                </Typography>
            )}
        </Box>
    );
};

export { DraggableFieldList };
